
import { Component, Prop, Vue } from 'vue-property-decorator'
import KAMReadOnlyForm from '@/views/kam/forms/KAMReadOnlyForm.vue'
import { booleanLabel, checkedItemsFromBooleanMap, dateLabel, ReadOnlySection, valueOrDash } from '@/views/kam/forms/kamReadOnlyPresenter'

@Component({
  components: { 'kam-read-only-form': KAMReadOnlyForm }
})
export default class KAM003V2 extends Vue {
  @Prop({ required: true }) answers!: Record<string, any>
  @Prop({ default: () => ({}) }) media!: Record<string, any>
  @Prop({ default: () => [] }) siteManagers!: any[]
  @Prop({ default: () => [] }) projectLeaders!: any[]
  @Prop({ default: () => [] }) constructionWorkers!: any[]
  @Prop({ default: () => [] }) employees!: any[]

  get vgItems () : string[] {
    return checkedItemsFromBooleanMap(this.answers?.vgItems, {
      lmra: 'LMRA',
      alarmCard: 'Alarmkaart',
      ppeUse: 'PBM gebruik',
      sharedFacilities: 'Gemeenschappelijke voorzieningen',
      firstAid: 'EHBO',
      incidents: 'Incident/ongevallen',
      ownEquipment: 'Gebruik (eigen) materieel',
      extinguishers: 'Blusmiddelen',
      dangerousSubstances: 'Gevaarlijke stoffen',
      hazardReportCard: 'Meldkaart gevaarlijke situatie',
      outsideWorkHours: 'Werken buiten werktijd',
      other: 'Overig'
    })
  }

  get siteItems () : string[] {
    return checkedItemsFromBooleanMap(this.answers?.siteItems, {
      accessRoute: 'Aanrijroute',
      parking: 'Parkeren',
      storageSpace: 'Opslagruimte',
      cleaning: 'Schoonhouden',
      craneSetup: 'Kraanopstelplaats',
      siteRoads: 'Bouwwegen (breedte)',
      siteCabins: 'Bouwketen',
      lightMasts: 'Lichtmasten',
      fencing: 'Afrastering',
      loadingUnloading: 'Laden/lossen',
      powerWater: 'Stroom / water',
      constructionWaste: 'Bouwafval',
      constructionLift: 'Bouwlift',
      escapeRoute: 'Vluchtroute',
      scaffold: 'Steiger',
      other: 'Overig'
    })
  }

  get docs () : string[] {
    return checkedItemsFromBooleanMap(this.answers?.docs, {
      signedOrder: 'Getekende opdracht',
      productSpec: 'Productspecificatie',
      workDrawings: 'Werktekeningen',
      productionDrawings: 'Productietekeningen',
      permit: 'Vergunning',
      workPlan: 'Werkplan',
      notApplicable: 'N.v.t.'
    })
  }

  get sections () : ReadOnlySection[] {
    const answers = this.answers || {}
    return [
      {
        key: 'project',
        title: 'Project',
        fields: [
          { key: 'projectName', label: 'Projectnaam', value: valueOrDash(answers.projectName) },
          { key: 'projectReference', label: 'Projectnummer', value: valueOrDash(answers.projectReference) },
          { key: 'projectDescription', label: 'Korte omschrijving', value: valueOrDash(answers.projectDescription) },
          { key: 'projectLocation', label: 'Adres / plaats', value: valueOrDash(answers.projectLocation) },
          { key: 'thunnissenPresent', label: 'Aanwezig namens Thunnissen', value: valueOrDash(answers.thunnissenPresent) },
          { key: 'thunnissenMobile', label: 'Mobiel Thunnissen', value: valueOrDash(answers.thunnissenMobile) },
          { key: 'meetingDate', label: 'Datum startgesprek', value: dateLabel(answers.meetingDate) }
        ]
      },
      {
        key: 'opdrachtnemer',
        title: 'Opdrachtnemer',
        fields: [
          { key: 'contractorName', label: 'Naam opdrachtnemer', value: valueOrDash(answers.contractorName) },
          { key: 'executingParty', label: 'Uitvoerende partij', value: valueOrDash(answers.executingParty) },
          { key: 'executingPartyNoAccount', label: 'Geen Woonon-account', value: answers.executingPartyNoAccount ? '☑' : '☐' },
          { key: 'orderNumber', label: 'Opdrachtnummer', value: valueOrDash(answers.orderNumber) },
          { key: 'disciplines', label: 'Discipline(s)', value: valueOrDash(answers.disciplines) },
          { key: 'vcaCertified', label: 'VCA gecertificeerd', value: booleanLabel(answers.vcaCertified) }
        ],
        tables: [
          {
            key: 'contractorPeople',
            label: 'Aanwezige personen opdrachtnemer',
            rows: Array.isArray(answers.contractorPeople) ? answers.contractorPeople : [],
            columns: [
              { key: 'name', label: 'Naam' },
              { key: 'company', label: 'Bedrijf' },
              { key: 'role', label: 'Functie' },
              { key: 'phone', label: 'Telefoon' },
              { key: 'bhv', label: 'BHV' },
              { key: 'presentAtMeeting', label: 'Aanwezig gesprek' },
              { key: 'presentOnSite', label: 'Aanwezig bouwplaats' }
            ]
          }
        ]
      },
      {
        key: 'besproken',
        title: 'Besproken onderdelen',
        fields: [
          { key: 'vgPlanReceived', label: 'VGM-plan ontvangen', value: booleanLabel(answers.vgPlanReceived) },
          { key: 'foremenMeetingPositive', label: 'Voormannenoverleg positief', value: booleanLabel(answers.foremenMeetingPositive) },
          { key: 'vgItems', label: 'VGM-onderwerpen', kind: 'array', value: this.vgItems },
          { key: 'vgOtherNotes', label: 'Toelichting VGM overig', value: valueOrDash(answers.vgOtherNotes) },
          { key: 'siteItems', label: 'Bouwplaatsonderwerpen', kind: 'array', value: this.siteItems },
          { key: 'siteOtherNotes', label: 'Toelichting bouwplaats overig', value: valueOrDash(answers.siteOtherNotes) },
          { key: 'docs', label: 'Documenten', kind: 'array', value: this.docs },
          { key: 'docsNotes', label: 'Toelichting documenten', value: valueOrDash(answers.docsNotes) }
        ]
      },
      {
        key: 'planning',
        title: 'Planning',
        fields: [
          { key: 'startDate', label: 'Startdatum', value: dateLabel(answers.startDate) },
          { key: 'planningDate', label: 'Planningsdatum', value: dateLabel(answers.planningDate) },
          { key: 'buildTempo', label: 'Bouwtempo', value: valueOrDash(answers.buildTempo) },
          { key: 'duration', label: 'Duur', value: valueOrDash(answers.duration) },
          { key: 'vacations', label: 'Vakanties', value: valueOrDash(answers.vacations) }
        ]
      },
      {
        key: 'acties',
        title: 'Acties',
        tables: [
          {
            key: 'actions',
            label: 'Actiepunten',
            rows: Array.isArray(answers.actions) ? answers.actions : [],
            columns: [
              { key: 'description', label: 'Omschrijving' },
              { key: 'owner', label: 'Eigenaar' },
              { key: 'when', label: 'Wanneer' },
              { key: 'done', label: 'Gereed' }
            ]
          }
        ]
      },
      {
        key: 'akkoord',
        title: 'Akkoord',
        fields: [
          { key: 'contractorApprovalName', label: 'Naam opdrachtnemer', value: valueOrDash(answers.contractorApprovalName) },
          { key: 'contractorApprovalDate', label: 'Datum akkoord opdrachtnemer', value: dateLabel(answers.contractorApprovalDate) },
          { key: 'contractorApprovalSignature', label: 'Handtekening opdrachtnemer aanwezig', value: booleanLabel(Boolean(answers.media?.contractorApprovalSignature)) },
          { key: 'thunnissenApprovalName', label: 'Naam Thunnissen', value: valueOrDash(answers.thunnissenApprovalName) },
          { key: 'thunnissenApprovalDate', label: 'Datum akkoord Thunnissen', value: dateLabel(answers.thunnissenApprovalDate) },
          { key: 'thunnissenApprovalSignature', label: 'Handtekening Thunnissen aanwezig', value: booleanLabel(Boolean(answers.media?.thunnissenApprovalSignature)) }
        ]
      }
    ]
  }
}
